// Sending someone off to buy a ticket for the train they are looking at.
//
// The board answers "what is coming"; it cannot answer "and how do I get on
// it". This builds the link that does, to traintickets.com — run by Atomised
// Co-operative Ltd, a small Scottish worker co-op that charges no booking fee
// and takes a share only of a split-ticket saving it actually finds.
//
// **This is an unpaid link.** There is no affiliate relationship, no
// commission, no tracking parameter, and nothing here is worth money to this
// site. That is worth stating in the code because the obvious future edit is
// to bolt a referral code onto SEARCH_URL, and the moment anybody does, the
// site owes readers a disclosure it does not currently owe them.
//
// Own module, importing nothing, for the reason pin-layout.ts and map-style.ts
// are: it is testable in isolation, and the date arithmetic below is the sort
// that is wrong twice a year and at midnight if nobody checks it.

/** Where a link goes when there is nothing more specific to say. */
export const SEARCH_URL = 'https://www.traintickets.com/';

/**
 * How to put a journey into a URL.
 *
 * **Deliberately null.** The parameter names are not documented anywhere
 * public, the site cannot be reached from the build environment to read them,
 * and a guess would produce a link that 404s for every reader while looking
 * perfectly fine in the code. A plain link to the search page always works;
 * a wrong deep link is worse than no deep link, because it fails on somebody
 * else's site and makes both parties look careless.
 *
 * To switch it on: run one search on traintickets.com, copy the resulting URL,
 * and fill this in. Nothing else changes — bookingUrl() and its tests already
 * cover both paths.
 *
 *   export const DEEP_LINK: DeepLinkFormat | null = {
 *     path: 'search',
 *     from: 'origin',
 *     to: 'destination',
 *     date: 'date',
 *     dateFormat: 'YYYY-MM-DD',
 *     time: 'time',
 *   };
 */
export interface DeepLinkFormat {
  /** Path under the origin, without a leading slash. */
  readonly path: string;
  readonly from: string;
  readonly to: string;
  readonly date: string;
  /** Only the two forms UK retailers actually use. */
  readonly dateFormat: 'YYYY-MM-DD' | 'YYYYMMDD' | 'DD/MM/YYYY';
  readonly time: string;
}

export const DEEP_LINK: DeepLinkFormat | null = null;

export interface BookingJourney {
  /** The station whose board this is. */
  readonly here: string;
  /** The other end: where a departure is going, or an arrival came from. */
  readonly other: string;
  /** Scheduled time at this station, "HH:MM". */
  readonly time: string;
  readonly direction: 'dep' | 'arr';
}

/**
 * Which way round the journey is.
 *
 * An arrivals board is not a list of journeys from here — it is a list of
 * journeys *to* here, and someone clicking one wants to travel inbound. Sending
 * them a search in the wrong direction would be confidently useless.
 */
export function endpoints(j: BookingJourney): { from: string; to: string } {
  return j.direction === 'dep'
    ? { from: j.here, to: j.other }
    : { from: j.other, to: j.here };
}

/**
 * Which day the service runs, in London.
 *
 * A departure board reaches about two hours ahead, so a time on it is almost
 * always today — but "almost always" is not a date. At 23:50 the board carries
 * 00:15 services, and dating those today would send someone to buy a ticket
 * for a train that left twenty-four hours ago.
 *
 * The rule is the direction of the gap, not its size: a board time far *behind*
 * the clock is tomorrow's, and one merely a few minutes behind is a train
 * running late, which is still today's. Six hours is comfortably past any
 * lateness the railway produces and comfortably short of a full day.
 */
export function journeyDate(time: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';

  const nowMinutes = (Number(get('hour')) % 24) * 60 + Number(get('minute'));
  const [h, m] = time.split(':').map(Number);
  const serviceMinutes = (h ?? 0) * 60 + (m ?? 0);

  // Built in UTC and read back in UTC purely as calendar arithmetic — the
  // London wall-clock date is already decided above, and re-reading it through
  // a timezone would move it back an hour on a summer evening.
  const date = new Date(Date.UTC(
    Number(get('year')), Number(get('month')) - 1, Number(get('day')),
  ));
  if (nowMinutes - serviceMinutes > 6 * 60) date.setUTCDate(date.getUTCDate() + 1);

  const y = date.getUTCFullYear();
  const mo = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

function formatDate(iso: string, format: DeepLinkFormat['dateFormat']): string {
  const [y, m, d] = iso.split('-');
  if (format === 'YYYYMMDD') return `${y}${m}${d}`;
  if (format === 'DD/MM/YYYY') return `${d}/${m}/${y}`;
  return iso;
}

/**
 * The link for one service.
 *
 * Falls back to the plain search page whenever a specific one cannot be built —
 * no format configured, no station names, an unparseable time. Every one of
 * those still leaves the reader somewhere useful, which a broken query string
 * would not.
 */
export function bookingUrl(j: BookingJourney, now: Date = new Date()): string {
  const { from, to } = endpoints(j);
  if (!DEEP_LINK || !from.trim() || !to.trim() || !/^\d{1,2}:\d{2}$/.test(j.time)) {
    return SEARCH_URL;
  }

  const url = new URL(DEEP_LINK.path, SEARCH_URL);
  // URLSearchParams encodes the names, so "King's Cross" and "Ashton-under-Lyne"
  // survive without any escaping of our own.
  url.searchParams.set(DEEP_LINK.from, from.trim());
  url.searchParams.set(DEEP_LINK.to, to.trim());
  url.searchParams.set(DEEP_LINK.date, formatDate(journeyDate(j.time, now), DEEP_LINK.dateFormat));
  url.searchParams.set(DEEP_LINK.time, j.time);
  return url.toString();
}

/** What the link should say it will do, for a title attribute. */
export function bookingLabel(j: BookingJourney): string {
  const { from, to } = endpoints(j);
  return from && to
    ? `Buy a ticket for ${from} to ${to} at traintickets.com`
    : 'Buy train tickets at traintickets.com';
}
